import { decryptEncryptedFile, fetchMatrixMedia } from "../utils";

/**
 * Extract a video thumbnail as either a Blob or a URL.
 *
 * @param {File|string} fileOrUrl - Video File object or video URL
 * @param {Object} options
 * @param {number} options.seekTo - Time to capture thumbnail from
 * @param {boolean} options.asBlob - If true, returns { blob, width, height }
 *                                  If false, returns thumbnailObjectUrl
 */
export function extractVideoThumbnail(fileOrUrl, { seekTo = 1.0, asBlob = false } = {}) {
    return new Promise((resolve, reject) => {
        const isFile = fileOrUrl instanceof File;
        const url = isFile ? URL.createObjectURL(fileOrUrl) : fileOrUrl;

        const video = document.createElement("video");
        video.src = url;
        video.muted = true;
        video.preload = "metadata";

        let hasSeeked = false;

        video.onloadedmetadata = () => {
            let target = Math.min(seekTo, video.duration - 0.1);
            if (target < 0) target = 0;

            try {
                video.currentTime = target;
            } catch {
                video.currentTime = 0;
            }
        };

        video.onseeked = () => {
            if (hasSeeked) return;
            hasSeeked = true;

            try {
                const canvas = document.createElement("canvas");
                canvas.width = video.videoWidth;
                canvas.height = video.videoHeight;

                const ctx = canvas.getContext("2d");
                ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

                canvas.toBlob((blob) => {
                    if (!blob) {
                        reject(new Error("Failed to generate thumbnail blob"));
                        return;
                    }

                    // ✔ Return raw blob result
                    if (asBlob) {
                        resolve({
                            blob,
                            width: canvas.width,
                            height: canvas.height,
                        });
                    } 
                    // ✔ Return object URL for display
                    else {
                        const thumbnailUrl = URL.createObjectURL(blob);
                        resolve(thumbnailUrl);
                    }

                    if (isFile) URL.revokeObjectURL(url);
                }, "image/jpeg", 0.75);

            } catch (err) {
                reject(err);
                if (isFile) URL.revokeObjectURL(url);
            }
        };

        video.onerror = () => {
            reject(new Error("Failed to load video for thumbnail"));
            if (isFile) URL.revokeObjectURL(url);
        };
    });
}

export const decryptEncryptedThumbnail = async (thumbnailFile, matrixClient) => {
    if (!thumbnailFile) return null;
    if (!matrixClient) return null;
    if (!thumbnailFile.url || !thumbnailFile.key || !thumbnailFile.iv) return null;

    try {
        const response = await fetchMatrixMedia(thumbnailFile.url, matrixClient);
        const blob = await response.blob();
        const decryptedBlob = await decryptEncryptedFile(blob, thumbnailFile, "image/jpeg");

        return URL.createObjectURL(decryptedBlob);
    } catch (err) {
        console.error("Failed to decrypt thumbnail", err);
        return null;
    }
};
